import { PASSWORD_MIN_LENGTH } from '../constants/password';

/** 0 empty, 1 too short, 2 weak, 3 good, 4 strong. */
export type PasswordLevel = 0 | 1 | 2 | 3 | 4;

export type PasswordStrength = {
  level: PasswordLevel;
  /** Characters still needed to reach the minimum, 0 once it is met. */
  missing: number;
};

const STRONG_LENGTH = 20;

// Compared against the whole password once it has been cleaned up. Lower case.
const COMMON_WORDS = new Set([
  'password',
  'qwerty',
  'qwertyuiop',
  'azerty',
  'asdfghjkl',
  'zxcvbnm',
  'letmein',
  'welcome',
  'iloveyou',
  'monkey',
  'dragon',
  'football',
  'sunshine',
  'princess',
  'admin',
  'sparklayer',
]);

const LEET: Record<string, string> = {
  '@': 'a',
  '4': 'a',
  '3': 'e',
  '1': 'i',
  '!': 'i',
  '0': 'o',
  $: 's',
  '5': 's',
};

const CHARACTER_KINDS = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z\d]/];

/** Drops digits and symbols from the ends, undoes leetspeak, then keeps only the letters. */
function cleanUp(password: string): string {
  return password
    .replace(/^[^a-zA-Z]+|[^a-zA-Z]+$/g, '')
    .toLowerCase()
    .replace(/[@431!0$5]/g, (char) => LEET[char] ?? char)
    .replace(/[^a-z]/g, '');
}

/** One character repeated, or a straight run up or down like "abcdef" or "987654". */
function isStraightRun(password: string): boolean {
  const points = [...password.toLowerCase()].map((char) => char.codePointAt(0) ?? 0);
  const step = (points[1] ?? 0) - (points[0] ?? 0);

  return (
    Math.abs(step) <= 1 &&
    points.every((point, index) => index === 0 || point - (points[index - 1] ?? 0) === step)
  );
}

function isEasyToGuess(password: string): boolean {
  return (
    /^\d+$/.test(password) ||
    new Set(password).size <= 3 ||
    isStraightRun(password) ||
    COMMON_WORDS.has(cleanUp(password))
  );
}

/** Advisory only, the forms enforce nothing beyond the minimum length. */
export function estimatePasswordStrength(password: string): PasswordStrength {
  // Counted the way the browser counts `minLength`, so the two agree on what is missing.
  const missing = Math.max(0, PASSWORD_MIN_LENGTH - password.length);

  if (!password) return { level: 0, missing };
  if (missing > 0) return { level: 1, missing };
  if (isEasyToGuess(password)) return { level: 2, missing };

  const kinds = CHARACTER_KINDS.filter((kind) => kind.test(password)).length;
  const points = [password.length >= 12, password.length >= 16, kinds >= 2, kinds >= 3].filter(
    Boolean,
  ).length;

  if (password.length >= STRONG_LENGTH || points === 4) return { level: 4, missing };
  return { level: points >= 2 ? 3 : 2, missing };
}
